import { useMutation, useQueryClient } from 'react-query'

import { attachmentRepository } from '@webapp/core/data-access/repositories/AttachmentRepository'
import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type CreditCardItemFormSchema } from '@webapp/modules/accounts-payable/components/CreditCardItemForm'
import { mapCreateCreditCardItemFormSchemaToPayload } from '@webapp/modules/accounts-payable/data-access/mappers/mapCreditCardItemFormSchemaToPayload'
import { creditCardItemRepository } from '@webapp/modules/accounts-payable/data-access/repositories/CreditCardItemRepository'
import { CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import {
  CREDIT_CARD_ITEM_LIST_BASE_KEY,
  CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY,
} from '@webapp/modules/accounts-payable/query-configs/creditCardItemQueries'

const readFileContent = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

// Attachment uploads happen inside the command flow (BHV-017) — UUIDs are
// collected into packing_slips before the create POST.
const uploadAttachments = async (files: File[]): Promise<string[]> => {
  const uploads = await Promise.all(
    files.map(async (file) =>
      attachmentRepository.upload({
        name: file.name,
        type: file.type,
        file: await readFileContent(file),
      })
    )
  )

  return uploads.map((attachment) => attachment.uuid)
}

interface CreateCreditCardItemVariables {
  creditcardId: number
  schema: CreditCardItemFormSchema
}

export const useMutateCreateCreditCardItem = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    CreditCardItem,
    RepoError,
    CreateCreditCardItemVariables
  >({
    mutationFn: async ({ creditcardId, schema }) => {
      const packingSlipUuids = await uploadAttachments(schema.attachments)

      const created = await creditCardItemRepository.create(
        mapCreateCreditCardItemFormSchemaToPayload({
          schema,
          creditcardId,
          packingSlipUuids,
        })
      )

      return new CreditCardItem(created)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_LIST_BASE_KEY,
      })
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY,
      })
    },
  })

  return {
    createCreditCardItem: mutation.mutateAsync,
    isCreating: mutation.isLoading,
    error: mutation.error,
  }
}
